import { useState } from 'react'

import useMsgs from '@/gf/hooks/useMsgs'
import { useTvhPartsQuery } from '@/suppliers/_gen/gql'
import useGqlClient from '@/suppliers/hooks/useGqlClient'
import useSession from '@/suppliers/hooks/useSession'
import useStores from '@/suppliers/hooks/useStores'

import Action from '@/gf/components/Action'
import AutocompleteSelect from '@/gf/components/AutocompleteSelect'
import Card from '@/gf/components/Card'
import Form from '@/gf/components/Form'
import TextInput from '@/gf/components/TextInput'
import TvhPartC from './Tvh/Part'

import type { LineItemPayload } from '@/types'
import type { TvhPart } from './Tvh/Part'

const TvhCard = ({
  orgId,
  onPartAdded,
}: {
  orgId?: string
  onPartAdded: (lineItems: LineItemPayload[]) => void
}) => {
  const [_, msgr] = useMsgs()
  const { user } = useSession()
  const allStores = useStores().data?.allStores || []
  const [term, setTerm] = useState('')
  const [searching, setSearching] = useState(false)
  const [parts, setParts] = useState<TvhPart[] | undefined>(undefined)
  const { refetch } = useTvhPartsQuery({ client: useGqlClient(), skip: true })
  const [storeId, setStoreId] = useState<string>()

  const storeOptions = allStores.map((sto) => ({
    value: sto.id,
    label: `${sto.name}${sto?.alias ? ` (${sto.alias})` : ''}`,
  }))

  const search = () => {
    setSearching(true)

    refetch({ term })
      .then(({ data }) => setParts(data.tvhParts))
      .catch(() => msgr.addUnknownError())
      .finally(() => setSearching(false))
  }

  return (
    <Card title="TVH Parts">
      <Card.Section>
        <Form onSubmit={search}>
          <div className="space-y-4">
            {user.role === 'admin' && (
              <AutocompleteSelect
                label=""
                name="storeSelection"
                className="w-full text-sm mb-1"
                value={allStores.find((s) => s.id === storeId)}
                options={storeOptions}
                onChange={(option) => setStoreId(option.value)}
                reactSelectOptions={{
                  isSearchable: true,
                  isMulti: false,
                  placeholder: 'Select store...',
                }}
              />
            )}

            <div className="flex gap-2">
              <TextInput value={term} onChange={({ target }) => setTerm(target.value)} />

              <Action.P performing={searching} type="submit">
                Search
              </Action.P>
            </div>
          </div>
        </Form>
      </Card.Section>

      {!searching && parts !== undefined && (
        <Card.Section>
          <div className="divide-y text-base">
            {parts.map((part) => (
              <TvhPartC
                part={part}
                orgId={orgId}
                storeId={storeId}
                onAdded={onPartAdded}
                key={part.mpn}
              />
            ))}
          </div>

          {parts.length === 0 && <div>No parts found.</div>}
        </Card.Section>
      )}
    </Card>
  )
}

export default TvhCard
